import { Toaster } from "./components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantBrandingProvider } from "./context/TenantBrandingContext";
import { ThemeProvider, bootstrapTheme } from "./context/ThemeContext";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import InstallPrompt from "./components/InstallPrompt";
import ImpersonationBanner from "./components/ImpersonationBanner";
import DeploymentVersionPill from "./components/DeploymentVersionPill";
import { installAutoDrain as installMediaAutoDrain } from "./lib/mediaOfflineQueue";
import { installUploadHandlers } from "./lib/uploadHandlers";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import GeminiLanding from "./pages/GeminiLanding";
import GeminiAdmin from "./pages/GeminiAdmin";
import TenantDBMigration from "./pages/TenantDBMigration";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Performance from "./pages/Performance";
import PlantUptimeDetail from "./pages/PlantUptimeDetail";
import CustomerSiteEditPage from "./pages/CustomerSiteEditPage";
import ReceiptScanPage from "./pages/ReceiptScanPage";
import SlipScannerPage from "./pages/SlipScannerPage";
import CustomerEditor from "./pages/CustomerEditor";
import SiteDetail from "./pages/SiteDetail";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import CallDetail from "./pages/CallDetail";
import Suspects from "./pages/Suspects";
import JobCards from "./pages/JobCards";
import JobCardDetail from "./pages/JobCardDetail";
import JobCardWizard from "./pages/service/JobCardWizard";
import JobCardTemplatesPage from "./pages/JobCardTemplatesPage";
import JobCardTemplateEditor from "./pages/JobCardTemplateEditor";
import JobcardSiteMismatch from "./pages/JobcardSiteMismatch";
import Team from "./pages/Team";
import EmployeeDetail from "./pages/EmployeeDetail";
import Me from "./pages/Me";
import SimplePaySync from "./pages/SimplePaySync";
import DocumentsExpiring from "./pages/DocumentsExpiring";
import ClockPage from "./pages/ClockPage";
import TeamPerformance from "./pages/TeamPerformance";
import TeamAnnouncements from "./pages/TeamAnnouncements";
import TeamCompetitions from "./pages/TeamCompetitions";
import TeamScorecard from "./pages/TeamScorecard";
import TeamCompetitionEditor from "./pages/TeamCompetitionEditor";
import TeamCompetitionDetail from "./pages/TeamCompetitionDetail";
import Recruitment from "./pages/Recruitment";
import RecruitmentPositionEditor from "./pages/RecruitmentPositionEditor";
import RecruitmentPositionDetail from "./pages/RecruitmentPositionDetail";
import RecruitmentCandidateNew from "./pages/RecruitmentCandidateNew";
import RecruitmentCandidateDetail from "./pages/RecruitmentCandidateDetail";
import DataImportIndex from "./pages/DataImportIndex";
import DataImportBatch from "./pages/DataImportBatch";
import Contracts from "./pages/Contracts";
import ContractDetail from "./pages/ContractDetail";
import PublicSign from "./pages/PublicSign";
import AttendanceRegister from "./pages/AttendanceRegister";
import AttendanceOverride from "./pages/AttendanceOverride";
import LeaveManager from "./pages/LeaveManager";
import LeaveApply from "./pages/LeaveApply";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import MeetingDetail from "./pages/MeetingDetail";
import HidroSettings from "./pages/HidroSettings";
import RolesEditor from "./pages/RolesEditor";
import HidroHealth from "./pages/HidroHealth";
import NotificationHealth from "./pages/NotificationHealth";
import Scheduler from "./pages/Scheduler";
import ServiceHistory from "./pages/ServiceHistory";
import Equipment from "./pages/Equipment";
import EquipmentAll from "./pages/EquipmentAll";
import PickingHub from "./pages/PickingHub";
import PickingListDetail from "./pages/PickingListDetail";
import EstimatorHub from "./pages/EstimatorHub";
import Gallery from "./pages/Gallery";
import Camera from "./pages/Camera";
import Projects from "./pages/Projects";
import ProjectWorkbench from "./pages/ProjectWorkbench";
import ProjectWorkbenchDetail from "./pages/ProjectWorkbenchDetail";
import WorkbenchPdfViewer from "./pages/WorkbenchPdfViewer";
import ProjectNew from "./pages/ProjectNew";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectDashboard from "./pages/ProjectDashboard";
import SalesDocEditor from "./pages/SalesDocEditor";
import ExpenseReviewQueue from "./pages/ExpenseReviewQueue";
import Supplier360 from "./pages/Supplier360";
import PortalDocument from "./pages/PortalDocument";
import GanttView from "./pages/GanttView";
import CalendarPage from "./pages/Calendar";
import MapView from "./pages/MapView";
import Tasks from "./pages/Tasks";
import TaskNew from "./pages/TaskNew";
import TaskDetail from "./pages/team/TaskDetail";
import CustomerNew from "./pages/CustomerNew";
import CallLogPage from "./pages/CallLogPage";
import Followups from "./pages/Followups";
import SettingsIndex from "./pages/settings";
import Trash from "./pages/Trash";
import ProfileSettings from "./pages/settings/ProfileSettings";
import TeamSettings from "./pages/settings/TeamSettings";
import OperationsSettings from "./pages/settings/OperationsSettings";
import PlatformSettings from "./pages/settings/PlatformSettings";
import PortalAdminHub from "./pages/PortalAdminHub";
import SalesSettings from "./pages/settings/SalesSettings";
import ServiceSettings from "./pages/settings/ServiceSettings";
import ProjectsSettings from "./pages/settings/ProjectsSettings";
import FinanceSettings from "./pages/settings/FinanceSettings";
import DocumentReviewQueue from "./pages/DocumentReviewQueue";
import OrderRequestsPage from "./pages/OrderRequestsPage";
import LeadOnboardingForm from "./pages/LeadOnboardingForm";
import ProcurementTrackers from "./pages/ProcurementTrackers";
import ProcurementTrackerDetail from "./pages/ProcurementTrackerDetail";
import OcrInvoicesPage from "./pages/OcrInvoicesPage";
import SupplierFullScanner from "./pages/SupplierFullScanner";
import AccountApplicationPublic from "./pages/AccountApplicationPublic";
import AccountApplicationsList from "./pages/AccountApplicationsList";
import ToolsIndex from "./pages/ToolsIndex";
import ToolsOnboarding from "./pages/ToolsOnboarding";
import ToolsWaterSheet from "./pages/ToolsWaterSheet";
import WaterSheetForm from "./pages/WaterSheetForm";
import Trips from "./pages/Trips";
import TripDetail from "./pages/TripDetail";
import ResearchLead from "./pages/ResearchLead";
import HidroStudy from "./pages/HidroStudy";
import DeepResearch from "./pages/DeepResearch";
import NewTicket from "./pages/NewTicket";
import SalesFunnel from "./pages/SalesFunnel";
import AnalyticsHub from "./pages/AnalyticsHub";
import InventoryCategoryEditor from "./pages/InventoryCategoryEditor";
import InventoryEnrichment from "./pages/InventoryEnrichment";
// Iter 228 — Users & Roles management moved to /settings/team.
// Route `/tools/users-roles` redirects (preserved for old bookmarks).
import EngineeringDesk from "./pages/EngineeringDesk";
import EngineeringSession from "./pages/EngineeringSession";
import EngineeringLibraryDetail from "./pages/EngineeringLibraryDetail";
import Suppliers from "./pages/Suppliers";
import SupplierNew from "./pages/SupplierNew";
import SupplierDetail from "./pages/SupplierDetail";
import PortalOrderNew from "./pages/PortalOrderNew";
import HidroAssistant from "./components/HidroAssistant";
import PortalLayout from "./components/PortalLayout";
import PortalRouteGuard from "./components/portal/PortalRouteGuard";
import { PortalPermsProvider } from "./context/PortalPermsContext";
import ClientPortal, { PortalPerformance } from "./pages/ClientPortal";
import PortalAnnouncements from "./pages/portal/PortalAnnouncements";
import CustomerPortalView from "./pages/CustomerPortalView";
import PortalJobCardDetail from "./pages/PortalJobCardDetail";
import PortalTicketDetail from "./pages/PortalTicketDetail";
import Inbox from "./pages/Inbox";
import PortalPhotos from "./pages/PortalPhotos";
import AlertsPage from "./pages/AlertsPage";
import ChatList from "./pages/ChatList";
import ChatDetail from "./pages/ChatDetail";
import MorePage from "./pages/MorePage";

// Map top-level URL segment to the permission that gates it. Owner
// bypasses via `hasPermission`. Segments not listed are permitted for
// any authenticated staff user (tools index, settings, equipment etc.
// are available to anyone with the tools nav OR by role-specific design).
const SEGMENT_PERMISSION = {
  "": "nav.dashboard",
  calendar: "nav.calendar",
  tasks: "nav.tasks",
  calls: "nav.calls",
  customers: "nav.customers",
  leads: "nav.leads",
  jobcards: "nav.jobcards",
  tickets: "nav.tickets",
  scheduler: "nav.schedule",
  equipment: "nav.schedule",
  ocr: "tools.ocr",
  research: "nav.leads",
  "sales-funnel": "nav.sales_funnel",
  "account-applications": "action.manage_account_applications",
};

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <BootstrapLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function BootstrapLoading() {
  // Show plain "Loading…" for 2.5 s then surface a hard escape so the
  // user can recover from a flaky network or stale service worker
  // without killing the PWA window. AuthContext itself bails to the
  // login form after 3 s — this is a belt-and-braces UI escape on top.
  // Reduced from 5 s to 2.5 s 2026-05 (iOS PWA login hang).
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStuck(true), 2_500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-[#647087] gap-4 px-6 text-center"
      data-testid="auth-bootstrap-loading"
    >
      <div className="text-sm">Loading…</div>
      {stuck && (
        <div className="max-w-sm space-y-3 mt-2" data-testid="auth-bootstrap-stuck">
          <div className="text-[11px] text-[#a0aabf]">
            Taking longer than usual. Network might be flaky or the app needs
            a refresh.
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="auth-bootstrap-reload"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem("hv_token");
                localStorage.removeItem("hv:user");
                window.location.href = "/login";
              }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="auth-bootstrap-relogin"
            >
              Sign in again
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                  }
                  if ("caches" in window) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                  }
                } catch {
                  /* ignore */
                }
                window.location.reload();
              }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="auth-bootstrap-clearcache"
            >
              Clear cache &amp; reload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  return children;
}

function ClientOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "client") return <Navigate to="/" replace />;
  return children;
}

/** Permission-based deep-link gate — redirects to dashboard if the user
 *  does NOT have the permission that guards the first URL segment. */
function RoleGate({ children }) {
  const { hasPermission, user } = useAuth();
  const loc = useLocation();
  if (user?.role === "owner") return children;
  const top = (loc.pathname.replace(/^\//, "").split("/")[0] || "");
  const need = SEGMENT_PERMISSION[top];
  // Unknown segments (e.g. /tools, /settings, /followups, /order-requests,
  // /procurement, /orders, /map) are treated as staff-general and allowed
  // — further fine-grained gates live inside each page via <RequirePerm>.
  if (need === undefined) return children;
  if (hasPermission(need)) return children;
  // Iter 400.3 — instead of silently bouncing to `/` (which looks like a
  // page refresh to the user), show a clear Access-denied screen that
  // names the missing permission. This is what surfaced the "rep still
  // cannot see job cards" bug — the tile refreshed home with no
  // explanation. Now the user sees exactly why AND how to escalate.
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-[#0a0b0e]"
      data-testid="role-gate-denied"
    >
      <div className="hv-card p-6 max-w-md text-center space-y-3">
        <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto">
          <span className="text-2xl">🔒</span>
        </div>
        <h2 className="font-display text-xl font-bold text-white">Access denied</h2>
        <p className="text-sm text-[#a0aabf]">
          Your account doesn&apos;t have the <code className="text-[#22d3ee] font-mono">{need}</code> permission
          needed to open <code className="text-[#22d3ee] font-mono">/{top}</code>.
        </p>
        <p className="text-xs text-[#647087]">
          Ask an owner or admin to enable this permission for your role
          under <strong>Settings → Users &amp; Roles</strong>.
        </p>
        <div className="flex gap-2 justify-center pt-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="role-gate-back"
          >
            Go back
          </button>
          <a href="/" className="hv-btn hv-btn-primary text-xs" data-testid="role-gate-home">
            Home
          </a>
        </div>
      </div>
    </div>
  );
}

// Inline wrapper used on specific routes where a permission must be
// enforced even when the URL segment is otherwise generic (e.g.
// `/tools/users-roles` — the segment is `tools`, but only owners with
// `tools.users_manager` should reach it).
function RequirePerm({ perm, children }) {
  const { hasPermission, user } = useAuth();
  if (user?.role === "owner") return children;
  if (!hasPermission(perm)) return <Navigate to="/" replace />;
  return children;
}

// Simple role-based gate (Wave 25 — Suppliers etc).
function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "owner") return children;
  if (Array.isArray(roles) && roles.includes(user.role)) return children;
  return <Navigate to="/" replace />;
}

function App() {
  useEffect(() => {
    installMediaAutoDrain();
    installUploadHandlers();
    bootstrapTheme();
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
      <TenantBrandingProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <InstallPrompt />
        <ImpersonationBanner />
        <DeploymentVersionPill />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* /_gemini — public demo landing (no auth required). */}
          <Route path="/_gemini" element={<GeminiLanding />} />
          <Route path="/_geminii/admin" element={<GeminiAdmin />} />
          {/* Manager portal preview — staff only, gated by password verify */}
          <Route
            path="/portal/preview/:customerId/*"
            element={
              <Protected>
                <CustomerPortalView isPreview />
              </Protected>
            }
          />
          {/* Public — no auth required */}
          <Route path="/apply/account" element={<AccountApplicationPublic />} />
          {/* Real client portal — only role=client */}
          <Route
            path="/portal"
            element={
              <Protected>
                <ClientOnly>
                  {/* [Iter 290zp.28] Wrap in ErrorBoundary so any
                      render error in a portal block (missing branding,
                      undefined field, etc.) shows a recovery card
                      instead of a completely black screen (bug the
                      user reported on production). */}
                  <ErrorBoundary>
                    <PortalPermsProvider>
                      <CustomerPortalView />
                    </PortalPermsProvider>
                  </ErrorBoundary>
                </ClientOnly>
              </Protected>
            }
          />
          {/* Iter 427.p40 — POPIA-safe direct-URL routes. Any portal
              user pasting `/portal/documents`, `/portal/jobs`,
              `/portal/tickets`, `/portal/team`, or `/portal/performance`
              directly into the address bar hits these routes. If their
              preset lacks the perm they get redirected to `/portal`
              (Overview) with a toast — never a blank page. Route
              elements render as `<Navigate to="/portal">` since the
              single-page CustomerPortalView already surfaces every
              section inline; the redirect just funnels legacy deep
              links back to the correct entry point. */}
          {[
            {path: "/portal/documents",   perm: "portal.documents.read"},
            {path: "/portal/jobs",        perm: "portal.jobs.read"},
            {path: "/portal/tickets",     perm: "portal.tickets.read"},
            {path: "/portal/team",        perm: "portal.users.manage"},
            // Iter 427.p45 — `/portal/performance` INTENTIONALLY not
            // in this redirect map. That path has its own full-page
            // `<PortalPerformance>` route below (line ~485) which we
            // want to win — funnelling it back to /portal would
            // stall on a black "Checking access…" flash while
            // permsCtx resolves.
          ].map(({path, perm}) => (
            <Route
              key={path}
              path={path}
              element={
                <Protected>
                  <ClientOnly>
                    <PortalPermsProvider>
                      <PortalRouteGuard perm={perm} redirectTo="/portal">
                        <Navigate to="/portal" replace />
                      </PortalRouteGuard>
                    </PortalPermsProvider>
                  </ClientOnly>
                </Protected>
              }
            />
          ))}
          {/* Wave 27.2 — public customer-facing quote/invoice portal (Phase C). */}
          <Route path="/portal/quote/:token" element={<PortalDocument kind="quote" />} />
          <Route path="/portal/invoice/:token" element={<PortalDocument kind="invoice" />} />
          {/* Wave 29 Chunk 5 — public e-signature surface (no auth required). */}
          <Route path="/sign/:token" element={<PublicSign />} />
          {/* Iter 427.p34 — public Water Purification Info Sheet (no auth). */}
          <Route path="/public/water-info-sheet/:token" element={<WaterSheetForm mode="public" />} />
          {/* Wave 26.2 — full-page customer-safe JC view. Open to BOTH
             real clients AND staff (staff in preview mode pass
             ?customer_id=<cid>). The page itself fetches from the
             scrubbed /api/portal/jobcards/{id} endpoint. */}
          <Route
            path="/portal/jobcards/:jid"
            element={
              <Protected>
                <PortalJobCardDetail />
              </Protected>
            }
          />
          {/* Iter 273 — portal-side full-page New Ticket editor. Same
             component as staff /tickets/new; it senses portal context from
             the URL and locks the customer to the signed-in client. Open to
             both real clients (role=client) and to staff in preview mode.
             MUST be declared BEFORE /portal/tickets/:tid so "new" doesn't
             match the :tid param. */}
          <Route
            path="/portal/tickets/new"
            element={
              <Protected>
                <NewTicket />
              </Protected>
            }
          />
          {/* Wave 26.4 — full-page customer-safe Ticket view. */}
          <Route
            path="/portal/tickets/:tid"
            element={
              <Protected>
                <PortalTicketDetail />
              </Protected>
            }
          />
          {/* Iter 273 ↑ */}
          {/* Full-page portal order storefront — open to clients AND to
             staff in preview (staff must pass ?customer_id=&as_staff=1). */}
          <Route
            path="/portal/order-new"
            element={
              <Protected>
                <PortalOrderNew />
              </Protected>
            }
          />
          {/* Iter 367 — Performance module for portal clients. */}
          <Route
            path="/portal/performance"
            element={
              <Protected>
                <ClientOnly>
                  <ErrorBoundary>
                    <PortalPerformance />
                  </ErrorBoundary>
                </ClientOnly>
              </Protected>
            }
          />
          {/* Iter 418.am — portal-side announcements history page. */}
          <Route
            path="/portal/announcements"
            element={
              <Protected>
                <ClientOnly>
                  <ErrorBoundary>
                    <PortalAnnouncements />
                  </ErrorBoundary>
                </ClientOnly>
              </Protected>
            }
          />
          {/* Legacy client portal sub-routes (jobs / tickets) — keep using PortalLayout for compatibility */}
          <Route
            path="/portal/legacy/*"
            element={
              <Protected>
                <ClientOnly>
                  <PortalLayout />
                </ClientOnly>
              </Protected>
            }
          >
            <Route path="*" element={<ClientPortal />} />
          </Route>
          <Route
            path="/"
            element={
              <Protected>
                <StaffOnly>
                  <RoleGate>
                    <Layout />
                  </RoleGate>
                </StaffOnly>
              </Protected>
            }
          >
            <Route index element={<Home />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="home" element={<Home />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="camera" element={<Camera />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/workbench" element={<ProjectWorkbench />} />
            <Route path="projects/workbench/:did" element={<ProjectWorkbenchDetail />} />
            <Route path="projects/workbench/:did/reports/:rid/pdf" element={<WorkbenchPdfViewer />} />
            <Route path="projects/new" element={<ProjectNew />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="projects/:id/dashboard" element={<ProjectDashboard />} />
            <Route path="customers/:cid/sales-docs/new" element={<SalesDocEditor mode="new" />} />
            <Route path="sales-documents/:did/edit" element={<SalesDocEditor mode="edit" />} />
            <Route path="expenses/review" element={<ExpenseReviewQueue />} />
            <Route path="suppliers/:id/360" element={<Supplier360 />} />
            <Route path="timeline" element={<GanttView />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="map" element={<MapView />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<CustomerNew />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="performance" element={<Performance />} />
            <Route path="performance/plant-uptime/:siteId" element={<PlantUptimeDetail />} />
            <Route path="customers/:id/edit" element={<CustomerEditor />} />
            <Route path="customers/:cid/sites/:sid" element={<SiteDetail />} />
            {/* 2026-02-18 Bug #8 — full-page site edit (no modal). */}
            <Route path="customers/:cid/sites/:sid/edit" element={<CustomerSiteEditPage />} />
            {/* 2026-02-18 Bug #1 — receipt scan tile + admin inbox. */}
            <Route path="receipt-scan" element={<ReceiptScanPage />} />
            {/* Iter 169 — Slip scanner with auto-OCR + admin queue. */}
            <Route path="slips" element={<SlipScannerPage />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="calls/:id" element={<CallDetail />} />
            <Route path="suspects" element={<Suspects />} />
            <Route path="leads/:id/onboarding" element={<LeadOnboardingForm />} />
            <Route path="jobcards" element={<JobCards />} />
            <Route path="jobcards/templates" element={<JobCardTemplatesPage />} />
            <Route path="jobcards/templates/new" element={<JobCardTemplateEditor />} />
            <Route path="jobcards/templates/:tid" element={<JobCardTemplateEditor />} />
            <Route path="jobcards/new" element={<JobCardWizard />} />
            <Route path="jobcards/:id" element={<JobCardDetail />} />
            <Route path="jobcards/:id/site-mismatch" element={<JobcardSiteMismatch />} />
            <Route path="team" element={<Team />} />
            <Route path="team/simplepay" element={<SimplePaySync />} />
            <Route path="team/documents-expiring" element={<DocumentsExpiring />} />
            <Route path="team/performance" element={<TeamPerformance />} />
            <Route path="team/announcements" element={<TeamAnnouncements />} />
            <Route path="team/competitions" element={<TeamCompetitions />} />
            <Route path="team/scorecard" element={<TeamScorecard />} />
            <Route path="team/competitions/new" element={<TeamCompetitionEditor />} />
            <Route path="team/competitions/:id" element={<TeamCompetitionDetail />} />
            <Route path="team/competitions/:id/edit" element={<TeamCompetitionEditor />} />
            <Route path="recruitment" element={<Recruitment />} />
            <Route path="recruitment/positions/new" element={<RecruitmentPositionEditor />} />
            <Route path="recruitment/positions/:pid" element={<RecruitmentPositionDetail />} />
            <Route path="recruitment/positions/:pid/edit" element={<RecruitmentPositionEditor />} />
            <Route path="recruitment/candidates/new" element={<RecruitmentCandidateNew />} />
            <Route path="recruitment/candidates/:cid" element={<RecruitmentCandidateDetail />} />
            <Route path="tools/import" element={<DataImportIndex />} />
            <Route path="tools/import/:bid" element={<DataImportBatch />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="contracts/:cid" element={<ContractDetail />} />
            <Route path="team/attendance" element={<AttendanceRegister />} />
            <Route path="team/attendance/:eid/override" element={<AttendanceOverride />} />
            <Route path="team/leave" element={<LeaveManager />} />
            <Route path="team/leave/new" element={<LeaveApply />} />
            <Route path="team/:uid" element={<EmployeeDetail />} />
            <Route path="me" element={<Me />} />
            <Route path="clock" element={<ClockPage />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="tickets/new" element={<NewTicket />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            <Route path="meetings/:id" element={<MeetingDetail />} />
            <Route path="settings/hidro" element={<HidroSettings />} />
            <Route path="settings/tenant-migration" element={<TenantDBMigration />} />
            <Route path="settings/roles" element={<RolesEditor />} />
            <Route path="settings/hidro/health" element={<HidroHealth />} />
            <Route path="settings/notifications" element={<NotificationHealth />} />
            <Route path="settings/document-review-queue" element={<DocumentReviewQueue />} />
            <Route path="scheduler" element={<Scheduler />} />
            <Route path="service-history" element={<ServiceHistory />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="equipment/all" element={<EquipmentAll />} />
            <Route path="picking" element={<PickingHub />} />
            <Route path="picking/lists/:lid" element={<PickingListDetail />} />
            <Route path="trash" element={<Trash />} />
            <Route path="estimator" element={<EstimatorHub />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="tasks/new" element={<TaskNew />} />
            <Route path="tasks/:id" element={<TaskDetail />} />
            <Route path="calls" element={<CallLogPage />} />
            <Route path="followups" element={<Followups />} />
            <Route path="order-requests" element={<OrderRequestsPage />} />
            <Route path="order-requests/:orderId" element={<OrderRequestsPage />} />
            <Route path="orders/:orderId" element={<OrderRequestsPage />} />
            <Route path="procurement" element={<ProcurementTrackers />} />
            <Route path="procurement/:tid" element={<ProcurementTrackerDetail />} />
            <Route path="ocr/invoices" element={<OcrInvoicesPage />} />
            <Route path="ocr/supplier-invoices" element={<SupplierFullScanner />} />
            <Route path="account-applications" element={<AccountApplicationsList />} />
            <Route path="tools" element={<ToolsIndex />} />
            <Route path="tools/onboarding" element={<ToolsOnboarding />} />
            <Route path="tools/water-info-sheet" element={<ToolsWaterSheet />} />
            <Route path="tools/water-info-sheet/:id" element={<WaterSheetForm mode="rep" />} />
            <Route path="trips" element={<Trips />} />
            <Route path="trips/:id" element={<TripDetail />} />
            <Route path="research" element={<DeepResearch />} />
            <Route path="research/lead/:id" element={<ResearchLead />} />
            <Route path="hidro/study" element={<HidroStudy />} />
            <Route path="sales-funnel" element={<SalesFunnel />} />
            <Route path="tools/analytics" element={<AnalyticsHub />} />
            <Route path="tools/inventory-categories" element={<InventoryCategoryEditor />} />
            <Route path="tools/inventory-enrichment" element={<RequireRole roles={["owner","admin","manager"]}><InventoryEnrichment /></RequireRole>} />
            <Route path="tools/engineering-desk" element={<EngineeringDesk />} />
            <Route path="tools/engineering-desk/:sid" element={<EngineeringSession />} />
            <Route path="tools/engineering-desk/library/:kind/:id" element={<EngineeringLibraryDetail />} />
            <Route path="tools/engineering-desk/library/:sid" element={<EngineeringSession />} />
            <Route path="suppliers" element={<RequireRole roles={["admin","manager"]}><Suppliers /></RequireRole>} />
            <Route path="suppliers/new" element={<RequireRole roles={["admin","manager"]}><SupplierNew /></RequireRole>} />
            <Route path="suppliers/:sid" element={<RequireRole roles={["admin","manager","rep","technician","support-officer"]}><SupplierDetail /></RequireRole>} />
            <Route
              path="tools/users-roles"
              element={<Navigate to="/settings/team" replace />}
            />
            <Route path="settings" element={<SettingsIndex />} />
            <Route path="settings/profile" element={<ProfileSettings />} />
            <Route path="settings/team" element={<TeamSettings />} />
            <Route path="settings/sales" element={<SalesSettings />} />
            <Route path="settings/service" element={<ServiceSettings />} />
            <Route path="settings/operations" element={<OperationsSettings />} />
            <Route path="settings/projects" element={<ProjectsSettings />} />
            <Route path="settings/finance" element={<FinanceSettings />} />
            <Route path="settings/platform" element={<PlatformSettings />} />
            <Route path="portal-admin" element={<PortalAdminHub />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="chat" element={<ChatList />} />
            <Route path="chat/:tid" element={<ChatDetail />} />
            <Route path="portal-photos" element={<PortalPhotos />} />
            <Route path="more" element={<MorePage />} />
          </Route>
        </Routes>
        <HidroAssistant />
      </BrowserRouter>
      </TenantBrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
